import {
  animate,
  AnimatePresence,
  m,
  useMotionValue,
  useTransform,
  type MotionValue,
} from 'motion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../format';
import { haptic } from '../telegram';
import { Icon } from '../ui/icons';

/** Ticket illustration: three stacked stubs fanning out. */
function Tickets() {
  return (
    <div className="relative h-40 w-56">
      {[-14, -2, 10].map((rot, i) => (
        <m.div
          key={rot}
          className="absolute inset-x-6 top-6 h-24 rounded-2xl bg-[image:var(--foil-soft)] shadow-[0_18px_40px_-18px_black]"
          initial={{ rotate: 0, y: 20, opacity: 0 }}
          animate={{ rotate: rot, y: i * -6, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.1 + i * 0.06 }}
        >
          <div className="absolute top-0 right-14 bottom-0 border-l-2 border-dashed border-foil-ink/25" />
        </m.div>
      ))}
    </div>
  );
}

function Seal({ children }: { children: ReactNode }) {
  return (
    <m.div
      className="grid size-36 place-items-center rounded-full border border-gold/40 text-gold shadow-[inset_0_0_40px_var(--color-gold-wash)]"
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24 }}
    >
      {children}
    </m.div>
  );
}

/** Long enough to read a slide; the bar at the top shows the time left. */
const SLIDE_MS = 5500;

/**
 * First-run intro, story style: each slide plays for a few seconds and moves on by
 * itself (the last one closes the intro). Swipe or tap the right/left side to go
 * forward/back, hold to pause — no button is required.
 */
export function Onboarding({
  vipReward,
  vipX,
  wheel,
  onDone,
}: {
  vipReward: number;
  vipX: number;
  wheel: boolean;
  onDone: () => void;
}) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const progress = useMotionValue(0);
  const slides = [
    { art: <Tickets />, title: t('app.onboarding.s1Title'), body: t('app.onboarding.s1Body') },
    {
      art: <Seal>{Icon.lock}</Seal>,
      title: t('app.onboarding.s2Title'),
      body: t('app.onboarding.s2Body'),
    },
    {
      art: <Seal>{wheel ? Icon.wheel : Icon.vip}</Seal>,
      title: t(wheel ? 'app.onboarding.s3Title' : 'app.onboarding.s3TitleNoWheel'),
      body: t(wheel ? 'app.onboarding.s3Body' : 'app.onboarding.s3BodyNoWheel', {
        vip_reward: vipReward,
        vip_x: vipX,
      }),
    },
  ];
  const last = i === slides.length - 1;
  const go = (to: number) => {
    if (to >= slides.length) return onDone();
    haptic.tick();
    progress.set(0);
    setI(Math.max(0, to));
  };

  // the timer: fill the current bar, then advance
  useEffect(() => {
    if (paused) return;
    const left = (1 - progress.get()) * SLIDE_MS;
    const c = animate(progress, 1, { duration: left / 1000, ease: 'linear' });
    void c.then(() => {
      if (progress.get() >= 1) go(i + 1);
    });
    return () => c.stop();
  }, [i, paused]); // eslint-disable-line react-hooks/exhaustive-deps

  // pause while the app is in the background
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const s = slides[i]!;
  const downAt = useRef(0);
  const dragged = useRef(false);

  return (
    <div className="grain fixed inset-0 z-50 flex flex-col bg-ink px-6 pt-[calc(14px+var(--safe-top))] pb-[calc(24px+var(--safe-bottom))] select-none">
      <div className="flex gap-1.5" aria-hidden>
        {slides.map((_, k) => (
          <span key={k} className="h-[3px] flex-1 overflow-hidden rounded-full bg-line-strong">
            {k < i ? (
              <span className="block h-full w-full bg-gold" />
            ) : k === i ? (
              <Bar progress={progress} />
            ) : null}
          </span>
        ))}
      </div>
      <div className="flex justify-end">
        <button onClick={onDone} className="h-10 text-sm text-muted active:text-text">
          {t('app.onboarding.skip')}
        </button>
      </div>
      <AnimatePresence mode="wait">
        <m.div
          key={i}
          role="group"
          aria-roledescription="slide"
          aria-label={`${i + 1} / ${slides.length}`}
          className="flex flex-1 cursor-pointer flex-col"
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onPointerDown={() => {
            downAt.current = Date.now();
            dragged.current = false;
            setPaused(true);
          }}
          onPointerUp={(e) => {
            setPaused(false);
            // a short tap navigates like stories: right side forward, left side back
            if (dragged.current || Date.now() - downAt.current > 250) return;
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            if (e.clientX - rect.left > rect.width * 0.35) go(i + 1);
            else go(i - 1);
          }}
          onPointerCancel={() => setPaused(false)}
          onDragStart={() => {
            dragged.current = true;
          }}
          onDragEnd={(_e, info) => {
            setPaused(false);
            if (info.offset.x < -60) go(i + 1);
            if (info.offset.x > 60 && i > 0) go(i - 1);
          }}
        >
          <div className="grid flex-1 place-items-center">{s.art}</div>
          <h1 className="display text-2xl leading-[1] font-semibold text-balance">{s.title}</h1>
          <p className="mt-4 max-w-[34ch] text-base leading-relaxed text-muted">{s.body}</p>
          <p className="mt-8 h-5 text-xs text-faint">
            {last ? t('app.onboarding.autoStart') : t('app.onboarding.hint')}
          </p>
        </m.div>
      </AnimatePresence>
    </div>
  );
}

function Bar({ progress }: { progress: MotionValue<number> }) {
  const width = useTransform(progress, (v) => `${v * 100}%`);
  return <m.span className="block h-full bg-gold" style={{ width }} />;
}
